import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Edit2, Trash2, Download, Save, X } from 'lucide-react';
import { api } from '../contexts/AuthContext';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const categories = ['Operations', 'Safety', 'Quality', 'Maintenance', 'Training', 'HR', 'Sales', 'Customer Service', 'Other'];

export default function SOPDetailPage() {
  const { sopId } = useParams();
  const navigate = useNavigate();
  const [sop, setSOP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ title: '', content: '', category: '' });

  useEffect(() => { fetchSOP(); }, [sopId]);

  const fetchSOP = async () => {
    try { const { data } = await api.get(`/api/sops/${sopId}`); setSOP(data); setEditData({ title: data.title, content: data.content, category: data.category }); }
    catch { toast.error('SOP not found'); navigate('/library'); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    try { const { data } = await api.put(`/api/sops/${sopId}`, editData); setSOP(data); setEditing(false); toast.success('SOP updated'); }
    catch { toast.error('Failed to update'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this SOP?')) return;
    try { await api.delete(`/api/sops/${sopId}`); toast.success('Deleted'); navigate('/library'); } catch { toast.error('Failed'); }
  };

  const exportPDF = () => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>${sop.title}</title><style>body{font-family:Inter,sans-serif;padding:40px;max-width:800px;margin:0 auto;}h1{font-size:24px;}h2{font-size:18px;margin-top:24px;}p{line-height:1.6;}</style></head><body><h1>${sop.title}</h1><p style="color:#666;">${sop.category} | ${new Date(sop.created_at).toLocaleDateString()}</p><div>${sop.content.replace(/\n/g,'<br/>')}</div></body></html>`);
    w.document.close(); w.print();
  };

  if (loading) return <div className="max-w-4xl mx-auto animate-pulse"><div className="h-8 bg-[#1F2937] rounded w-1/3 mb-4" /><div className="h-4 bg-[#1F2937] rounded w-1/4 mb-8" /><div className="space-y-3">{[1,2,3].map(i=><div key={i} className="h-4 bg-[#1F2937] rounded" />)}</div></div>;
  if (!sop) return null;

  return (
    <div className="max-w-4xl mx-auto" data-testid="sop-detail-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/library')} className="gap-1 text-[#9CA3AF] hover:text-[#F9FAFB] hover:bg-[#1F2937]" data-testid="back-to-library-btn"><ArrowLeft size={16} /> Library</Button>
      </div>

      {editing ? (
        <div className="space-y-4">
          <div className="bg-[#111827] rounded-lg border border-[#374151] p-6">
            <input value={editData.title} onChange={(e) => setEditData({...editData, title: e.target.value})} className="text-2xl font-semibold text-[#F9FAFB] w-full border-none outline-none bg-transparent mb-4" data-testid="edit-sop-title" />
            <Select value={editData.category} onValueChange={(v) => setEditData({...editData, category: v})}>
              <SelectTrigger className="w-48 mb-4 bg-[#1F2937] border-[#374151] text-[#F9FAFB]" data-testid="edit-sop-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#374151]">{categories.map(c => <SelectItem key={c} value={c} className="text-[#F9FAFB] focus:bg-[#1F2937]">{c}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea value={editData.content} onChange={(e) => setEditData({...editData, content: e.target.value})} className="min-h-[500px] text-sm bg-[#1F2937] border-[#374151] text-[#F9FAFB]" data-testid="edit-sop-content" />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setEditing(false)} className="gap-2 border-[#374151] text-[#9CA3AF] hover:bg-[#1F2937]" data-testid="cancel-edit-btn"><X size={16} /> Cancel</Button>
            <Button onClick={handleSave} className="bg-[#AAFF00] text-[#080812] hover:bg-[#c4ff4d] gap-2 font-semibold" data-testid="save-edit-btn"><Save size={16} /> Save</Button>
          </div>
        </div>
      ) : (
        <div className="bg-[#111827] rounded-lg border border-[#374151] p-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight" data-testid="sop-title">{sop.title}</h1>
          <p className="text-sm text-[#9CA3AF] mt-2 mb-6">
            <span className="px-2 py-0.5 bg-[#1F2937] rounded text-xs font-medium text-[#F9FAFB] border border-[#374151]">{sop.category}</span>
            <span className="ml-3">Created {new Date(sop.created_at).toLocaleDateString()}</span>
          </p>
          <div className="flex gap-2 mb-6 pb-6 border-b border-[#374151]">
            <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="gap-1.5 border-[#374151] text-[#9CA3AF] hover:bg-[#1F2937] hover:text-[#F9FAFB]" data-testid="edit-sop-btn"><Edit2 size={14} /> Edit</Button>
            <Button variant="outline" size="sm" onClick={exportPDF} className="gap-1.5 border-[#374151] text-[#9CA3AF] hover:bg-[#1F2937] hover:text-[#F9FAFB]" data-testid="export-pdf-btn"><Download size={14} /> Export</Button>
            <Button variant="outline" size="sm" onClick={handleDelete} className="gap-1.5 border-[#374151] text-red-400 hover:bg-red-500/10" data-testid="delete-sop-btn"><Trash2 size={14} /> Delete</Button>
          </div>
          <div className="prose prose-invert prose-sm max-w-none text-[#F9FAFB]/90 leading-relaxed" data-testid="sop-content">
            <ReactMarkdown>{sop.content}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}
